import { useEffect, useState, type ReactNode } from "react";
import { helpers } from "../util/helpers.ts";
import { formatSeasonRecord } from "../util/seasonRecord.ts";
import { createMobileCrossTabHref } from "../util/mobileCrossTabLink.ts";
import { getCols } from "../util/getCols.ts";
import useTitleBar from "../hooks/useTitleBar.tsx";
import { DataTable } from "../components/DataTable/index.tsx";
import i18n from "../util/i18n.ts";
import { wrappedTeamLogoAndName } from "../components/TeamLogoAndName.tsx";
import type { View } from "../../../../common/types.ts";
import type { DataTableRow } from "../components/DataTable/index.tsx";
import { gradientStyleFactory } from "../util/gradientStyleFactory.ts";
import { prefixStatOpp } from "../util/prefixStatOpp.ts";
import { PlusMinus } from "../components/PlusMinus.tsx";
import TeamRankingTable, {
  type TeamRankingColumn,
  type TeamRankingSection,
} from "../components/TeamRankingTable.tsx";
import {
  formatTeamRankingPercentage,
  getTeamRankingSeasonStarted,
} from "../components/teamRankingTableState.ts";
import { TeamRosterSheet } from "../components/TeamRosterSheet.tsx";
import {
  filterPolicyKeys,
  teamStatsDefaultStats,
  teamStatsMobileBaseColNames,
} from "../mobile/displayPolicies.ts";
import {
  MobileContentHero,
  MobileExpertDisclosure,
  MobileStatRow,
} from "../mobile/mobilePrimitives.tsx";
import { MobileStatInsights } from "../mobile/MobileStatInsights.tsx";
import { isCountHighlightRenderable } from "../mobile/statMeaningfulness.ts";
import {
  getInsightStatValue,
  getLeagueRankEvidence,
  getTeamStatStrengthWeaknessConclusions,
} from "../mobile/statInsights.ts";
import {
  getP3V1CompactTeamRanking,
  getP3V1TeamMetricRanks,
  getP3V1TeamStrengths,
  getP3V1TeamWeaknesses,
  getP3V1UserTeamStanding,
} from "../util/statInsight.ts";
import { translateTeamName, translateTeamRegion } from "../util/teamNames.ts";
import { lowerIsBetter } from "../../../../common/lowerIsBetter.ts";

const formatMaybeInteger = (x: number) =>
  Number.isInteger(x) ? String(x) : x.toFixed(1);

type DataTableCellObject = {
  value: ReactNode;
};

const isDataTableCellObject = (
  value: DataTableRow["data"][number],
): value is DataTableCellObject =>
  typeof value === "object" && value !== null && Object.hasOwn(value, "value");

const getCellValue = (value: DataTableRow["data"][number]): ReactNode =>
  isDataTableCellObject(value) ? value.value : (value as ReactNode);

const TeamStats = ({
  allStats,
  averages,
  playoffs,
  season,
  stats,
  teamOpponent,
  teams,
  ties,
  otl,
  usePts,
  userTid,
}: View<"teamStats">) => {
  useTitleBar({
    title: i18n.t("teamStats.title", { ns: "ui", defaultValue: "Team Stats" }),
    jumpTo: !window.mobile,
    jumpToSeason: season,
    dropdownView: "team_stats",
    dropdownFields: {
      seasons: season,
      teamOpponentAdvanced: teamOpponent,
      playoffs,
    },
  });

  const [expertOpen, setExpertOpen] = useState(() => !window.mobile);
  const [rosterSheetTid, setRosterSheetTid] = useState<number | undefined>();

  // A team/opponent scope route change restores that viewport's initial depth.
  // Season and other data refreshes preserve the user's manual disclosure choice.
  useEffect(() => {
    setExpertOpen(!window.mobile);
  }, [teamOpponent]);

  // B3 column convergence: on mobile the full table keeps the 10-column team
  // budget (4 base + 6 default stats); desktop keeps every column.
  const useMobileColumnBudget = !!window.mobile;
  const basicColNames: string[] = useMobileColumnBudget
    ? [...teamStatsMobileBaseColNames]
    : ["#", "Team", "stat:gp", "W", "L"];
  if (!useMobileColumnBudget) {
    if (otl) {
      basicColNames.push("OTL");
    }
    if (ties) {
      basicColNames.push("T");
    }
    if (usePts) {
      basicColNames.push("PTS", "PTS%");
    } else {
      basicColNames.push("%");
    }
    basicColNames.push("AvgAge");
  }

  const visibleStats = filterPolicyKeys(stats, teamStatsDefaultStats);

  const cols = getCols(
    [
      ...basicColNames,
      ...visibleStats.map((stat) => {
        if (stat.startsWith("opp")) {
          return `stat:${stat.charAt(3).toLowerCase()}${stat.slice(4)}`;
        }
        return stat === "pos" ? "Pos" : `stat:${stat}`;
      }),
    ],
    {
      "#": {
        sortSequence: [],
        noSearch: true,
      },
    },
  );

  if (teamOpponent.endsWith("ShotLocations") && visibleStats.length >= 7) {
    cols.at(-7)!.title = "M";
    cols.at(-6)!.title = "A";
    cols.at(-5)!.title = "%";
  }

  const otherStatColumns = ["won", "lost", "age"];
  if (otl) {
    otherStatColumns.push("otl");
  }
  if (ties) {
    otherStatColumns.push("tied");
  }
  if (usePts) {
    otherStatColumns.push("pts", "ptsPct");
  } else {
    otherStatColumns.push("winp");
  }

  const gradientStyle = gradientStyleFactory(
    1,
    Math.round(0.35 * teams.length),
    Math.round(0.65 * teams.length),
    teams.length,
  );

  // gradientStyleFactory maps HIGH x to the success color, but league ranks
  // are 1-based best-first (rank 1 = best, matching getP3V1TeamMetricRanks in
  // util/statInsight.ts), so invert: rank 1 → x = teams.length (full green).
  const gradientXFromRank = (rank: number) => teams.length + 1 - rank;

  const makeRowObject = (
    teamStats: (typeof teams)[number]["stats"],
    teamSeasonAttrs: (typeof teams)[number]["seasonAttrs"],
  ) => {
    const data: { [key: string]: DataTableRow["data"][number] } = {};

    if (!useMobileColumnBudget) {
      data.gp = formatMaybeInteger(teamStats.gp);
    }
    data.won = formatMaybeInteger(teamSeasonAttrs.won);
    data.lost = formatMaybeInteger(teamSeasonAttrs.lost);

    if (!useMobileColumnBudget) {
      if (otl) {
        data.otl = formatMaybeInteger(teamSeasonAttrs.otl);
      }
      if (ties) {
        data.tied = formatMaybeInteger(teamSeasonAttrs.tied);
      }
      if (usePts) {
        data.ptsPts = Math.round(teamSeasonAttrs.pts);
        data.ptsPct = helpers.roundWinp(teamSeasonAttrs.ptsPct);
      } else {
        data.winp = helpers.roundWinp(teamSeasonAttrs.winp);
      }

      data.avgAge =
        teamSeasonAttrs.avgAge !== undefined
          ? teamSeasonAttrs.avgAge.toFixed(1)
          : null;
    }

    for (const stat of visibleStats) {
      const value = Object.hasOwn(teamStats, stat)
        ? (teamStats as any)[stat]
        : (teamSeasonAttrs as any)[stat];
      data[stat] = helpers.roundStat(value, stat);
    }

    const plusMinusCols = [prefixStatOpp(teamOpponent, "mov"), "nrtg"];
    for (const plusMinusCol of plusMinusCols) {
      if (Object.hasOwn(data, plusMinusCol)) {
        data[plusMinusCol] = (
          <PlusMinus>{(teamStats as any)[plusMinusCol]}</PlusMinus>
        );
      }
    }

    return data;
  };

  const rows = teams.map((t) => {
    const data = makeRowObject(t.stats, t.seasonAttrs);

    // This is our team.
    if (userTid === t.tid) {
      // Color stat values accordingly.
      for (const [statType, value] of Object.entries(data)) {
        if (
          (!visibleStats.includes(statType) &&
            !otherStatColumns.includes(statType)) ||
          !allStats[statType]
        ) {
          continue;
        }

        // Determine our team's percentile for this stat type. allStats arrays
        // are sorted best-first (see server/worker/views/teamStats.ts), so the
        // league rank is index + 1 — the same 1-based convention as
        // getP3V1TeamMetricRanks.
        const statTypeValue = Object.hasOwn(t.stats, statType)
          ? (t.stats as any)[statType]
          : (t.seasonAttrs as any)[statType];
        const rank = allStats[statType].indexOf(statTypeValue) + 1;

        data[statType] = {
          style: gradientStyle(gradientXFromRank(rank)),
          value: getCellValue(value),
        };
      }
    }

    return {
      key: t.tid,
      data: [
        null,
        wrappedTeamLogoAndName(
          t,
          createMobileCrossTabHref(
            helpers.leagueUrl([
              "roster",
              `${t.seasonAttrs.abbrev}_${t.tid}`,
              season,
            ]),
          ),
        ),
        ...Object.values(data),
      ],
    };
  });

  const footer = averages
    ? {
        data: [
          null,
          i18n.t("teamStats.avg", { ns: "ui", defaultValue: "Avg" }),
          ...Object.values(makeRowObject(averages as any, averages as any)),
        ],
      }
    : undefined;
  const keyMetricRanks = getP3V1TeamMetricRanks(
    teams,
    allStats as Record<string, readonly number[]>,
    visibleStats,
    userTid,
  );
  const teamStrengths = getP3V1TeamStrengths(keyMetricRanks);
  const teamWeaknesses = getP3V1TeamWeaknesses(
    keyMetricRanks,
    3,
    new Set(teamStrengths.map((metric) => metric.stat)),
  );
  // C4 data-story conclusions: a strength/weakness row only renders when it is
  // a genuine top/bottom league rank with enough games behind it (statInsights
  // gates); below the gate the whole section does not render (A3 precedent).
  const userTeamGamesPlayed = getInsightStatValue(
    teams.find((t) => t.tid === userTid)?.stats.gp,
  );
  const teamStatConclusions = getTeamStatStrengthWeaknessConclusions({
    gamesPlayed: userTeamGamesPlayed,
    strengths: teamStrengths,
    teamCount: teams.length,
    // With ≤3 visible metrics the legacy split parks every metric in
    // teamStrengths and leaves teamWeaknesses empty, so the weakness filter
    // scans the union — the top/bottom rank thresholds keep the two sides
    // disjoint in any league bigger than 2 × topThreshold.
    weaknesses: [...teamWeaknesses, ...teamStrengths],
  });
  const getTeamStatMetricValue = (t: (typeof teams)[number], stat: string) =>
    getInsightStatValue(
      Object.hasOwn(t.stats, stat)
        ? (t.stats as any)[stat]
        : (t.seasonAttrs as any)[stat],
    );
  // Conclusion labels: the column title of an opp-prefixed stat drops the
  // "opponent" context ("oppPts" → "PTS"), so the six default team stats get
  // self-contained statInsights labels; anything else falls back to the
  // column title. Margin-type values carry their sign.
  const teamStatInsightLabelDefaults: Record<string, string> = {
    drtg: "Defensive rating",
    mov: "Average point margin",
    nrtg: "Net rating",
    oppPts: "Points allowed per game",
    ortg: "Offensive rating",
    pts: "Points scored per game",
  };
  const getTeamStatInsightLabel = (stat: string) =>
    teamStatInsightLabelDefaults[stat]
      ? i18n.t(`statInsights.statLabel.${stat}`, {
          ns: "ui",
          defaultValue: teamStatInsightLabelDefaults[stat],
        })
      : getTeamStatLabel(stat);
  const formatTeamInsightValue = (stat: string, value: number) =>
    (stat === "mov" || stat === "nrtg") && value >= 0
      ? `+${formatTeamMetricValue(stat, value)}`
      : formatTeamMetricValue(stat, value);
  const getTeamStatInsightItem = (
    metric: (typeof keyMetricRanks)[number],
    tone: "positive" | "warning",
  ) => ({
    conclusion: `${getTeamStatInsightLabel(metric.stat)} ${formatTeamInsightValue(
      metric.stat,
      metric.value,
    )} · ${i18n.t("statInsights.leagueRank", {
      ns: "ui",
      defaultValue: "league #{{rank}}",
      rank: metric.rank,
    })}`,
    evidence: getLeagueRankEvidence({
      entries: teams,
      getValue: (t) => getTeamStatMetricValue(t, metric.stat),
      isSubject: (t) => t.tid === userTid,
      lowerIsBetter: lowerIsBetter.has(metric.stat),
    }).map((row) => (
      <MobileStatRow
        highlighted={row.isSubject}
        key={row.subject.tid}
        label={`${translateTeamRegion(row.subject.seasonAttrs.region as string)} ${translateTeamName(row.subject.seasonAttrs.name as string)}`}
        rank={`#${row.rank}`}
        value={formatTeamInsightValue(metric.stat, row.value)}
      />
    )),
    id: `team-stats-${tone === "positive" ? "strength" : "weakness"}-${metric.stat}`,
    tone,
  });
  const compactTeamRanking = getP3V1CompactTeamRanking(teams);
  const compactRankingColumns: TeamRankingColumn[] = [
    {
      key: "wins",
      kind: "number",
      label: i18n.t("standings.wins", { ns: "ui", defaultValue: "W" }),
    },
    {
      key: "losses",
      kind: "number",
      label: i18n.t("standings.losses", { ns: "ui", defaultValue: "L" }),
    },
    {
      key: "winPercentage",
      kind: "percentage",
      label: i18n.t("teamStats.mobileRankingMetric", {
        ns: "ui",
        defaultValue: "Win %",
      }),
    },
  ];
  const compactRankingSections: TeamRankingSection[] = [
    {
      id: "all",
      rows: compactTeamRanking.map((team, index) => ({
        imgURL: team.seasonAttrs.imgURL,
        imgURLSmall: team.seasonAttrs.imgURLSmall,
        isUserTeam: team.tid === userTid,
        rank: index + 1,
        teamName: `${translateTeamRegion(team.seasonAttrs.region)} ${translateTeamName(
          team.seasonAttrs.name,
        )}`,
        tid: team.tid,
        values: {
          losses: formatMaybeInteger(team.seasonAttrs.lost as number),
          winPercentage: formatTeamRankingPercentage(
            team.seasonAttrs.winp as number,
          ),
          wins: formatMaybeInteger(team.seasonAttrs.won as number),
        },
      })),
      zone: "none",
    },
  ];
  const rankingSeasonStarted = getTeamRankingSeasonStarted(
    teams.map((team) => ({
      gp: team.stats.gp,
      lost: team.seasonAttrs.lost,
      otl: team.seasonAttrs.otl,
      tied: team.seasonAttrs.tied,
      won: team.seasonAttrs.won,
    })),
  );
  // R6-3: an explicit "your team" anchor line so the user's standing reads at a
  // glance even when they sit below the top-8 compact ranking.
  const userTeamStanding = getP3V1UserTeamStanding(teams, userTid);
  const teamStatsScopeValue = i18n.t("teamStats.mobileScopeSeason", {
    ns: "ui",
    defaultValue: "{{season}} season stats",
    season,
  });
  // ② N3-A: the opponent view keeps a one-line explainer, but the audit-tone
  // wording ("explain defensive pressure before rankings") is replaced with
  // plain player language; the team view uses the scope label.
  const teamStatsExplainer = teamOpponent.toLowerCase().includes("opp")
    ? i18n.t("teamStats.p2s09OpponentExplainer", {
        ns: "ui",
        defaultValue:
          "What opponents scored against each team — lower is better.",
      })
    : teamStatsScopeValue;
  const getTeamStatColumnKey = (stat: string) => {
    if (stat.startsWith("opp")) {
      return `stat:${stat.charAt(3).toLowerCase()}${stat.slice(4)}`;
    }
    return stat === "pos" ? "Pos" : `stat:${stat}`;
  };
  const getTeamStatLabel = (stat: string) =>
    getCols([getTeamStatColumnKey(stat)])[0]?.title ?? stat;
  const formatTeamMetricValue = (stat: string, value: number) =>
    helpers.roundStat(value, stat);
  const getTeamRecord = (team: (typeof teams)[number]) =>
    formatSeasonRecord(team.seasonAttrs);

  // B2a content-first migration (v5Content contract): the first screen IS the
  // compact team ranking rows, then the own-team strengths/weaknesses rows.
  // The only narration is the single hero explainer line; scope chips, the
  // data-story hero, and the insight strip are gone.
  return (
    <>
      <div
        data-host-state={teams.length === 0 ? "empty" : "ready"}
        data-host-state-host="teamStats"
        data-host-state-row-count={teams.length}
      >
        {/* G2e: the data-center hub tab strip retired; a single breadcrumb back to
            the converged Data entry (League Leaders) replaces it. */}
        <a
          className="btn btn-link btn-sm px-0 mb-2 d-inline-flex align-items-center gap-1"
          data-data-return-link=""
          href={helpers.leagueUrl(["leaders"])}
        >
          <span aria-hidden="true">←</span>
          {i18n.t("dataHub.returnToData", {
            ns: "ui",
            defaultValue: "Back to data",
          })}
        </a>
        <MobileContentHero
          className="mb-3"
          data-key-metric-ranks={keyMetricRanks.length}
          data-team-strengths-hero=""
          data-v5-teamstats-ranking-first-screen=""
          explainer={teamStatsExplainer}
        >
          {userTeamStanding && rankingSeasonStarted ? (
            <MobileStatRow
              className="mb-3"
              data-user-team-standing={userTeamStanding.rank}
              highlighted
              label={i18n.t("teamStats.mobileUserTeamAnchorLabel", {
                ns: "ui",
                defaultValue: "Your team",
              })}
              rank={`#${userTeamStanding.rank}`}
              value={
                userTeamStanding.mov !== undefined
                  ? i18n.t("teamStats.mobileUserTeamMargin", {
                      ns: "ui",
                      defaultValue: "{{margin}} margin",
                      margin: formatTeamInsightValue(
                        "mov",
                        userTeamStanding.mov,
                      ),
                    })
                  : getTeamRecord(userTeamStanding.team)
              }
            />
          ) : null}
          <section className="mb-3" data-compact-team-ranking="">
            <TeamRankingTable
              ariaLabel={i18n.t("teamStats.title", {
                ns: "ui",
                defaultValue: "Team Stats",
              })}
              columns={compactRankingColumns}
              onTeamSelect={setRosterSheetTid}
              rankLabel={i18n.t("standings.rankLabel", {
                ns: "ui",
                defaultValue: "Rank",
              })}
              seasonNotStartedLabel={i18n.t("standings.seasonNotStarted", {
                ns: "ui",
                defaultValue: "The season has not started",
              })}
              seasonStarted={rankingSeasonStarted}
              sections={compactRankingSections}
              teamLabel={i18n.t("teamStats.mobileRankingTeamHeader", {
                ns: "ui",
                defaultValue: "Team",
              })}
            />
          </section>
        </MobileContentHero>

        {/* A3: count-0 highlight sections do not render (isCountHighlightRenderable).
            C4: strengths/weaknesses only render as data-story conclusions — a
            genuine top/bottom league rank behind the games gate, tap to expand
            the league evidence rows for that metric. */}
        {isCountHighlightRenderable(teamStatConclusions.strengths.length) ||
        isCountHighlightRenderable(teamStatConclusions.weaknesses.length) ? (
          <section className="row mb-3">
            {isCountHighlightRenderable(
              teamStatConclusions.strengths.length,
            ) ? (
              <div className="col-12 col-md-6" data-top-three-strengths="">
                <h2>
                  {i18n.t("teamStats.mobileTopStrengths", {
                    ns: "ui",
                    defaultValue: "Top strengths",
                  })}
                </h2>
                <MobileStatInsights
                  items={teamStatConclusions.strengths.map((metric) =>
                    getTeamStatInsightItem(metric, "positive"),
                  )}
                />
              </div>
            ) : null}
            {isCountHighlightRenderable(
              teamStatConclusions.weaknesses.length,
            ) ? (
              <div className="col-12 col-md-6" data-top-three-weaknesses="">
                <h2>
                  {i18n.t("teamStats.mobileTopWeaknesses", {
                    ns: "ui",
                    defaultValue: "Top weaknesses",
                  })}
                </h2>
                <MobileStatInsights
                  items={teamStatConclusions.weaknesses.map((metric) =>
                    getTeamStatInsightItem(metric, "warning"),
                  )}
                />
                <div className="mt-2">
                  <a
                    className="btn btn-light-bordered btn-sm"
                    href={createMobileCrossTabHref(
                      helpers.leagueUrl(["free_agents"]),
                    )}
                  >
                    {i18n.t("teamStats.p1s07MarketAction", {
                      ns: "ui",
                      defaultValue: "Check market fit",
                    })}
                  </a>
                </div>
              </div>
            ) : null}
          </section>
        ) : null}

        <MobileExpertDisclosure
          className="mb-3"
          count={i18n.t("teamStats.mobileAdvancedCount", {
            ns: "ui",
            defaultValue: "{{count}} teams",
            count: rows.length,
          })}
          data-advanced-table-depth=""
          expertHostId="team-stats-complete-table"
          onToggle={(event) => {
            setExpertOpen((event.currentTarget as HTMLDetailsElement).open);
          }}
          open={expertOpen}
          summary={i18n.t("teamStats.mobileAdvancedSummary", {
            ns: "ui",
            defaultValue: "Complete team stat table",
          })}
        >
          {/* B3: the DataTable is the single expert-depth carrier — the pruned
              card list on mobile, the unchanged table on desktop. The
              duplicate summary copy is gone (法则二 / DataTable mobile exit).
              B4: lazy-mounted — only rendered once the disclosure is open. */}
          {expertOpen ? (
            <DataTable
              cols={cols}
              defaultSort={[useMobileColumnBudget ? 2 : 3, "desc"]}
              defaultStickyCols={useMobileColumnBudget ? 0 : 2}
              mobileP3Export={{
                label: i18n.t("teamStats.title", {
                  ns: "ui",
                  defaultValue: "Team Stats",
                }),
                season,
              }}
              name={`TeamStats${teamOpponent}`}
              rankCol={0}
              rows={rows}
              footer={footer}
            />
          ) : null}
        </MobileExpertDisclosure>
      </div>
      <TeamRosterSheet
        onClose={() => setRosterSheetTid(undefined)}
        open={rosterSheetTid !== undefined}
        season={season}
        tid={rosterSheetTid}
      />
    </>
  );
};

export default TeamStats;
